import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector} from 'react-redux';
import { amountFormatter, customerServiceNumberFormatted } from '../../utility/utils/formatter';
import Globals from '../../utility/constants/appConstants';

const InvoicesPopUp = ({
  invoices,
  labels,
  handleCloseModal,
  invoiceNum,
  data,
  modalRef,
  serviceDetailsCss,
  approachScreen
}) => {
  // Initialize from Storage (same as class state initialization)
  const customerSummary = useSelector((state) => state.login.customerInfo);
  const [fullAdd] = useState(customerSummary?.[0].ServiceAddresses[0].city + ', ' + customerSummary?.[0].ServiceAddresses[0].state + ', ' + customerSummary?.[0].ServiceAddresses[0].country + ', ' + customerSummary?.[0].ServiceAddresses[0].postalCode || "");
  const [custAdd] = useState(customerSummary?.[0].ServiceAddresses[0].address || "");
  const [customerNumber] = useState(customerSummary?.[0].CustomerNumber || "");
  const [accountHolderName] = useState(customerSummary?.[0] || {});
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!isPrinting) {
      return undefined;
    }

    // Scopes the #root-hiding print CSS to this invoice print flow only,
    // so other pages' print buttons aren't affected.
    document.body.classList.add('printing-invoice-document');


    const resetPrinting = () => setIsPrinting(false);
    window.addEventListener('afterprint', resetPrinting, { once: true });
    window.print();

    return () => {
      document.body.classList.remove('printing-invoice-document');
      window.removeEventListener('afterprint', resetPrinting);
    };
  }, [isPrinting]);

  const printPage = useCallback((e) => {
    e.preventDefault();
    setIsPrinting(true);
  }, []);

  // data can be a single invoice object (legacy) or an array of invoice/sales agreement lines sharing an invoice number
  const invoiceLines = Array.isArray(data) ? data : (data ? [data] : []);
  const primaryInvoice = invoiceLines[0];

  // Only some callers provide totalAmt/invoice.invoiceBalance; others already provide pre-formatted invAmtIntPart/invAmtFractPart
  const hasStructuredAmount = invoiceLines.some(
    (line) => line?.totalAmt != null || line?.invoice?.invoiceBalance != null
  );
  const totalInvoiceAmount = invoiceLines.reduce(
    (sum, line) => sum + (Number(line?.totalAmt ?? line?.invoice?.invoiceBalance) || 0),
    0
  );
  const totalAmtFormatted = amountFormatter(totalInvoiceAmount);

  const handleServiceticketUrl = useCallback(
    (e) => {
      e.preventDefault();
      window.open(primaryInvoice?.ticketLink);
    },
    [primaryInvoice]
  );

  return (
    <div className="mma-wrapper">
      <div
        ref={modalRef}
        className={`${serviceDetailsCss['invoice-detail-modal']} modal`}
        tabIndex="-1"
        role="dialog"
        data-bs-backdrop="static"
        style={{zIndex:99999}}
      >
        <div className={`modal-dialog ${serviceDetailsCss['modal-dialog-invoice']} modal-dialog-centered`}>
          <div className={`modal-content ${serviceDetailsCss['modal-content-invoice']}`}>
            <div className="w-100">
              <div className={`wrapper ${serviceDetailsCss['wrapper-invoice']}`}>
                <div className="row">
                  <div className={`${serviceDetailsCss['left-white']}`}>
                    <p>
                      {labels?.invoiceLabel}
                      <span id="InvoiceNumber" className="fillable">
                        {primaryInvoice?.invoiceNumber}
                      </span>
                    </p>
                  </div>
                  <div className={`${serviceDetailsCss['fold']}`}>
                    <a
                      href="#"
                      className={`${serviceDetailsCss['ntrclose-button']} float-right`}
                      data-bs-dismiss="modal"
                      aria-label="Close"
                      dangerouslySetInnerHTML={{ __html: labels?.crossMark }}
                      onClick={handleCloseModal}
                    />
                  </div>
                </div>
              </div>

              <div className={`${serviceDetailsCss['white-wrapper']}`}>
                <div className={`${serviceDetailsCss['bg-white']} p-30`} role="document">
                  <div id="content" className={`wrapper ${serviceDetailsCss['wrapper-invoice']}`}>
                    <section className={`${serviceDetailsCss['main']}`}>
                      <div className={`${serviceDetailsCss['addr']}`}>
                        <div id="FullName" className={`${serviceDetailsCss['name']}`}>
                          {(accountHolderName?.BusinessName || '')}
                        </div>
                        <div className={`${serviceDetailsCss['cust-number']}`}>
                          {labels?.customerNumber}{' '}
                          <span id="CustomerNumber" className="fillable">
                            {customerNumber}
                          </span>
                        </div>
                        {(approachScreen !== 'oneTime' || (approachScreen === 'oneTime' && invoiceLines.length == 1)) && (
                          <div className="custom-address">
                          {labels?.serviceAddress}
                          <div id="ServiceAddressStreet" className="fillable">
                            {custAdd}
                          </div>
                          </div>
                        )}
                      </div>

                      <div className={`${serviceDetailsCss['invoice-amount']}`}>
                        <p className={`${serviceDetailsCss['amount-title']}`}>{labels?.invoiceAmount}</p>
                        <p id="InvoiceAmount" className={`${serviceDetailsCss['amount']}`}>
                          {hasStructuredAmount ? (
                            <>
                              {Globals.currencySymbol}{totalAmtFormatted.dollars}
                              <sup className="tg-ml-11">{' ' + totalAmtFormatted.cents}</sup>
                            </>
                          ) : (
                            <>
                              {primaryInvoice?.invAmtIntPart}
                              <sup className="tg-ml-11">{' ' + primaryInvoice?.invAmtFractPart}</sup>
                            </>
                          )}
                        </p>
                      </div>

                      <div className={`${serviceDetailsCss['invoice-status']}`}>
                        {primaryInvoice?.invoiceOpen === 'N' && (
                          <div
                            id="InvoicePaidContainer"
                            className={`InvoiceBalanceType ${serviceDetailsCss['invoice-status-paid']}`}
                          >
                            <div className="the-invoice-status">
                              <h2>{labels?.paid}</h2>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    <section className={`${serviceDetailsCss['service']}`}>
                      {invoiceLines.length > 1 ? (
                        <div className={`${serviceDetailsCss['service-table-wrapper']}`}>
                          <table className={`${serviceDetailsCss['service-table']}`}>
                            <thead>
                              <tr>
                                <th>{labels?.serviceName || 'Service Name'}</th>
                                <th>{labels?.serviceDate}</th>
                                <th>{labels?.amount || 'Amount'}</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {invoiceLines.map((line, idx) => {
                                const lineAmtFormatted = hasStructuredAmount
                                  ? amountFormatter(Number(line?.totalAmt ?? line?.invoice?.invoiceBalance) || 0)
                                  : null;
                                return (
                                  <tr key={`${line?.invoiceNumber}-${idx}`}>
                                    <td className={`${serviceDetailsCss['service-title']} fillable`}>{line?.desc}</td>
                                    <td className={`${serviceDetailsCss['service-date']}`}>{line?.date}</td>
                                    <td>
                                      {hasStructuredAmount
                                        ? `${Globals.currencySymbol}${lineAmtFormatted.dollars}.${lineAmtFormatted.cents}`
                                        : `${line?.invAmtIntPart || ''}.${(line?.invAmtFractPart || '').toString().trim()}`}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className={`btn btn-primary btn-warning ${serviceDetailsCss['view-invoice-ticket']}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          window.open(line?.ticketLink);
                                        }}
                                      >
                                        {labels?.viewServiceTicket}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        invoiceLines.map((line, idx) => (
                          <div className="service-listing" key={`${line?.invoiceNumber}-${idx}`}>
                            <p id="ServiceName" className={`${serviceDetailsCss['service-title']} fillable`}>
                              {line?.desc}
                            </p>
                            <p className={`${serviceDetailsCss['service-date']}`}>
                              {labels?.serviceDate}{' '}
                              <span id="WorkOrderServiceDate">{line?.date}</span>
                            </p>
                          </div>
                        ))
                      )}
                    </section>

                    <section className={`d-flex justify-content-between ${serviceDetailsCss['view-pint-btn']}`}>
                      <div className="button-container">
                        {invoiceLines.length <= 1 && (
                          <button
                            id="ServiceTicket"
                            className={`btn btn-primary btn-warning ${serviceDetailsCss['view-invoice-ticket']}`}
                            type="button"
                            onClick={handleServiceticketUrl}
                          >
                            {labels?.viewServiceTicket}
                          </button>
                        )}
                      </div>
                      <div className="button-container last">
                        <button
                          id="PrintInvoiceButton"
                          className={`btn ${serviceDetailsCss['print-invoice']}`}
                          type="button"
                          onClick={printPage}
                        >
                          {labels?.printThisPage}
                        </button>
                      </div>
                    </section>
                  </div>

                  <div id="footer" className="wrapper">
                    <footer>
                      <p className={`text-center mt-4 ${serviceDetailsCss['service-num']}`}>
                        {labels?.footerSection}
                        <a
                          href={`tel:${customerServiceNumberFormatted}`}
                          className="js-phoneno"
                          title={`Call ${customerServiceNumberFormatted}`}
                        >
                          {customerServiceNumberFormatted}
                        </a>
                      </p>
                    </footer>
                  </div>

                  {createPortal(
                    <article className={`invoice-print-document${isPrinting ? ' is-printing' : ''}`} aria-hidden="true">
                    <header className="invoice-print-header">
                      <div>
                        <img src="/images/header/logo.svg" alt="TruGreen" className="invoice-print-logo" />
                        <section className="invoice-print-recipient">
                          <div>{accountHolderName?.BusinessName || ''}</div>
                         {(approachScreen !== 'oneTime' || (approachScreen === 'oneTime' && invoiceLines.length == 1)) && (
                          <>
                            <div>{custAdd}</div>
                            <div>{fullAdd}</div>
                          </>
                        )}
                        </section>
                      </div>
                      <div className="invoice-print-summary">
                        <strong>Invoice</strong>
                        <dl>
                          <div>
                            <dt>{labels?.customerNumber || 'Customer Number :'}</dt>
                            <dd>{customerNumber}</dd>
                          </div>
                          <div>
                            <dt>{labels?.invoiceDate || 'Invoice Date :'}</dt>
                            <dd>{primaryInvoice?.invoice?.invoiceDate ? primaryInvoice?.invoice.invoiceDate : primaryInvoice?.invoiceDate}</dd>
                          </div>
                          <div>
                            <dt>{labels?.totalDue || 'Total Due :'}</dt>
                            <dd>
                              {hasStructuredAmount
                                ? `${Globals.currencySymbol}${totalAmtFormatted.dollars}.${totalAmtFormatted.cents}`
                                : `${primaryInvoice?.invAmtIntPart || ''}.${(primaryInvoice?.invAmtFractPart || '').toString().trim()}`}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </header>

                    <section className="invoice-print-heading">
                      <h1>Your TruGreen Invoice</h1>
                      <div>
                        <strong>Pay by phone</strong>
                        <a href={`tel:${customerServiceNumberFormatted}`}>{customerServiceNumberFormatted}</a>
                      </div>
                      <div>
                        <strong>Questions</strong>
                        <a href={`tel:${customerServiceNumberFormatted}`}>{customerServiceNumberFormatted}</a>
                      </div>
                    </section>

                    <table className="invoice-print-table">
                      <thead>
                        <tr>
                          <th>{labels?.serviceDate || 'Service Date'}</th>
                          <th>{labels?.serviceDescription || 'Description of Services'}</th>
                          <th>{labels?.invoiceNumber || 'Invoice Number'}</th>
                          <th>Total Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceLines.map((line, idx) => {
                          const lineAmtFormatted = hasStructuredAmount
                            ? amountFormatter(Number(line?.totalAmt ?? line?.invoice?.invoiceBalance) || 0)
                            : null;
                          return (
                            <tr key={`print-${line?.invoiceNumber}-${idx}`}>
                              <td>{line?.date}</td>
                              <td>{line?.desc}</td>
                              <td>{line?.invoiceNumber}</td>
                              <td>
                                {hasStructuredAmount
                                  ? `${Globals.currencySymbol}${lineAmtFormatted.dollars}.${lineAmtFormatted.cents}`
                                  : `${line?.invAmtIntPart || ''}.${(line?.invAmtFractPart || '').toString().trim()}`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </article>,
                    document.body
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>  
    </div>
  );
};

export default InvoicesPopUp;
